
import React from 'react';
import { Article } from '@/data/articlesData';
import { Link } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from '@/components/ui/button';
import { CalendarDays, Clock, ArrowRight, Share2, Bookmark } from 'lucide-react';
import OptimizedImage from '@/components/ui/optimized-image';

interface FeaturedArticleProps {
  article: Article;
}

const FeaturedArticle2: React.FC<FeaturedArticleProps> = ({ article }) => {
  return (
    <Card className="overflow-hidden mb-6 md:mb-8 shadow-lg border-0 group hover:shadow-xl transition-all duration-300">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Image section - reduced from lg:col-span-3 to lg:col-span-2 for mobile optimization */}
        <div className="relative">
          <div className="aspect-[16/9] lg:aspect-[4/3] relative overflow-hidden">
            {article.image && (
              <OptimizedImage
                src={article.image}
                alt={article.title}
                fill={true}
                priority={true}
                sizes="(max-width: 768px) 100vw, 50vw"
                objectFit="cover"
                quality={90}
                containerClassName="w-full h-full"
                className="group-hover:scale-105 transition-transform duration-700"
              />
            )}
            
            {/* Enhanced gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            
            {/* Featured badge */}
            <div className="absolute top-3 md:top-4 left-3 md:left-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-1 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold shadow-lg">
              ⭐ Suosituin artikkeli
            </div>
            
            {/* Action buttons */}
            <div className="absolute top-3 md:top-4 right-3 md:right-4 flex space-x-2">
              <button 
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-all duration-200"
                onClick={(e) => {
                  e.preventDefault();
                  if (navigator.share) {
                    navigator.share({
                      title: article.title,
                      url: `${window.location.origin}/artikkelit/${article.slug}`
                    });
                  }
                }}
                aria-label="Jaa artikkeli"
              >
                <Share2 size={16} className="text-gray-700" />
              </button>
              <button 
                className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-white transition-all duration-200"
                aria-label="Tallenna artikkeli"
              >
                <Bookmark size={16} className="text-gray-700" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Content section - increased from lg:col-span-4 to lg:col-span-2 */}
        <CardContent className="p-4 md:p-6 lg:p-8 flex flex-col justify-between bg-gradient-to-br from-blue-50 via-white to-blue-50/50">
          <div>
            {/* Meta information */}
            <div className="flex items-center gap-4 text-xs md:text-sm text-gray-500 mb-4">
              <div className="flex items-center">
                <CalendarDays className="h-4 w-4 mr-1" />
                <span>
                  {new Date(article.date).toLocaleDateString('fi-FI', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
              {article.readTime && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{article.readTime}</span>
                </div>
              )}
            </div>
            
            {/* Title */}
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4 text-gray-900 leading-tight">
              <Link 
                to={`/artikkelit/${article.slug}`}
                className="hover:text-blue-600 transition-colors duration-200"
              >
                {article.title}
              </Link>
            </h2>
            
            {/* Summary */}
            <p className="text-gray-700 mb-4 text-sm md:text-base leading-relaxed">
              {article.summary || article.description.substring(0, 150) + '...'}
            </p>
            
            {/* Extended preview - mobile optimized */}
            <div className="mb-6 hidden md:block">
              <p className="text-gray-600 line-clamp-3 text-sm leading-relaxed">
                {article.description.split('.').slice(0, 2).join('. ') + '.'}
              </p>
            </div>
          </div>
          
          {/* CTA section */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              asChild
              size="default" 
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex-1 sm:flex-none"
            >
              <Link to={`/artikkelit/${article.slug}`} className="inline-flex items-center justify-center">
                Lue artikkeli 
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            
            <Button 
              variant="outline" 
              size="default"
              className="border-blue-200 text-blue-600 hover:bg-blue-50 font-medium px-4 py-3 rounded-lg"
              onClick={() => {
                document.getElementById('article-grid')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Näytä lisää
            </Button>
          </div>
        </CardContent>
      </div>
    </Card>
  );
};

export default FeaturedArticle2;
